import {Component, OnInit} from '@angular/core';
import {AuthService} from '../../services/auth/auth.service';
import {combineLatest, take} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {User} from '../../model/user';
import {UserLoadActions} from '../../store/user/actions/userActions';
import {ActivatedRoute} from '@angular/router';
import isUUID from 'validator/es/lib/isUUID';
import {UserService} from '../../services/user/user.service';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {TranslateService} from '@ngx-translate/core';
import {CustomValidators} from '../../validator/custom-validator';
import {FormBuilder} from '@angular/forms';
import {CompanyService} from '../../services/company-service/company.service';
import {selectMyCompany} from '../../store/company/selectors/company.selectors';
import {CompanyPage} from '../../model/company-page';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {DialogTwoOptionsComponent} from '../../components/dialogs/two-options-dialog/dialog-two-options.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserPage} from '../../model/user-page';
import {NotificationPreferencesService} from '../../services/push-notifications/notification-preferences.service';
import {NotificationChannel} from '../../enum/notifications/notification-channel';
import {NotificationType} from '../../enum/notifications/notification-type';
import {SkendataService} from '../../services/skendata-service/skendata.service';

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss', '../../../styles/component-custom-style.scss', '../../../styles/form-input.scss'],
    standalone: false
})
export class AccountComponent implements OnInit {

  notificationsVisible: boolean = true;
  kisSwitch: boolean = false;
  skenDataSwitch: boolean = true;
  roles$ = this.userStore.select(selectUserRoles);
  lang: string = '';
  languages = ['en', 'de', 'it'];
  representatives: User[] = [];
  selectedRepresentative: User | null = null;
  notificationsNumberProposal: number;
  notificationsNumberOffer: number;
  notificationsNumberMemberArea: number;
  totalNumberNotificationsProposal: number;
  totalNumberNotificationsOffer: number;
  totalNumberNotificationsMemberArea: number;
  notificationsProposalVisible: boolean;
  notificationsOfferVisible: boolean;
  notificationsMemberAreaVisible: boolean;

  userId: string;
  user$ = this.userStore.select(selectUser);
  user: User = null;

  kisFormGroup = this.fb.group({
    username: ['', [CustomValidators.notOnlyWhitespace]],
    password: ['', [CustomValidators.notOnlyWhitespace]],
  });
  skenDataFormGroup = this.fb.group({
    skenDataAccountId: ['', [CustomValidators.notOnlyWhitespace]]
  });

  compareFn = (a: User | null | undefined, b: User | null | undefined): boolean => {
    return a?.id === b?.id;
  };
  constructor(readonly authService: AuthService,
              private readonly userStore: Store<User>,
              private readonly activatedRoute: ActivatedRoute,
              private readonly userService: UserService,
              private readonly translateService: TranslateService,
              private readonly fb: FormBuilder,
              private readonly companyService: CompanyService,
              private readonly dialog: MatDialog,
              private readonly snackbar: MatSnackBar,
              private readonly notificationPreferencesService: NotificationPreferencesService,
              private readonly skendataService: SkendataService
  ) {
  }

  ngOnInit(): void {
    this.skenDataFormGroup.disable();

    this.lang = localStorage.getItem('lang') || navigator.language.slice(0,2);
    this.translateService.use(this.lang);

    let userId: string = this.activatedRoute.snapshot.params['id'];
    this.userId = userId;
    let hasUser: boolean;
    this.userStore.select(selectUser).subscribe(user => {
      hasUser = user != null;
      this.user = user;
    });

    if (isUUID(userId) && hasUser) {
      this.userStore.dispatch(UserLoadActions.load({userId: userId}));
    }

    this.userStore.select(selectMyCompany).subscribe((company) => {
      if (company.kisUsername) {
        this.kisFormGroup.patchValue({username: company.kisUsername, password: '*******'});
        this.kisSwitch = true;
      }
    })
    this.userStore.select(selectUser).subscribe((user) => {
      if (user.skenDataAccountId) {
        this.skenDataFormGroup.patchValue({skenDataAccountId: user.skenDataAccountId});
      }
    })

    combineLatest([
      this.notificationPreferencesService.findAllByUserId(this.userId).pipe(take(1)),
      this.userStore.select(selectUser).pipe(take(1))
    ]).subscribe(([preferences, user]) => {
      const allDisabled = preferences.every(preference => preference.allDisabled);
      this.notificationsVisible = !allDisabled;

      // user notification selected number
      this.notificationsNumberProposal = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.PROPOSAL, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled
      ).length;

      this.notificationsNumberOffer = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.OFFER, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled
      ).length;

      this.notificationsNumberMemberArea = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.MEMBER_AREA, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled).length;

      // total number
      this.totalNumberNotificationsProposal = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.PROPOSAL, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative)
      ).length;

      this.totalNumberNotificationsOffer = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.OFFER, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative)
      ).length;

      this.totalNumberNotificationsMemberArea = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.MEMBER_AREA, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative)
      ).length;

      // expand notifications area
      this.notificationsProposalVisible = this.notificationsNumberProposal > 0;
      this.notificationsOfferVisible = this.notificationsNumberOffer > 0;
      this.notificationsMemberAreaVisible = this.notificationsNumberMemberArea > 0;
    });


    this.userService.getUsersFromTheSameCompany(userId).subscribe(users => {
      this.representatives = users;
      if (this.user.userRepresentativeId !== null)
        this.selectedRepresentative = this.representatives.find(rep => rep.id === this.user.userRepresentativeId);
      else
        this.selectedRepresentative = null;
    });

  }

  resetPassword() {
    this.authService.resetPassword();
  }

  toggleKisSwitch() {
    this.kisSwitch = !this.kisSwitch;
  }

  toggleSkenDataSwitch() {
    this.skenDataSwitch = !this.skenDataSwitch;
  }

  protected readonly Roles = Roles;

  changeLanguage() {
    localStorage.setItem('lang', this.lang);
    this.translateService.use(this.lang);
    this.userStore.select(selectUser).subscribe(user => {
      let updatedUser = new UserPage(user);
      updatedUser.language = this.lang;
      this.userService.updateUserProfile(this.userId, updatedUser).subscribe();
    });
  }

  changeRepresentative() {
    this.userService.updateUserRepresentative(this.userId, this.selectedRepresentative ? this.selectedRepresentative.id : '').subscribe(() => {
        this.userStore.dispatch(UserLoadActions.load({userId: this.userId}));
        this.snackbar.open(this.translateService.instant('account.menu.representative.dialog.toastSuccess'), '', {
          panelClass: ['success-snackbar', 'custom-snackbar'],
        });
      },
      (_) => {
        this.snackbar.open(this.translateService.instant('account.menu.representative.dialog.toastFail'), '', {
          duration: 3000,
          panelClass: ['fail-snackbar', 'custom-snackbar']
        });
      });
  }

  saveKisCredentials() {
    let companyPage: CompanyPage = new CompanyPage({
      kisUsername: this.kisFormGroup.value.username,
      kisPassword: this.kisFormGroup.value.password
    });
    this.userStore.select(selectMyCompany).subscribe(company => {
      this.companyService.updateCompanyPage(company.companyId, companyPage).subscribe(() => {
        this.snackbar.open(this.translateService.instant('account.menu.kis.dialog.toast'), '', {
          panelClass: ['success-snackbar', 'custom-snackbar'],
        })
      });
    });
  }
  openConfirmationMatDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: this.translateService.instant('account.menu.kis.dialog.description'),
      optionCancel: this.translateService.instant('account.menu.kis.dialog.no'),
      optionConfirm: this.translateService.instant('account.menu.kis.dialog.yes'),
    };
    const dialogRef = this.dialog.open(DialogTwoOptionsComponent, dialogConfig);
    dialogRef.afterClosed()?.subscribe((result) => {
      if (result) {
        this.saveKisCredentials();
      }
    });
  }

  toggleNotificationsVisible() {
    this.notificationsVisible = !this.notificationsVisible;
    combineLatest([
      this.notificationPreferencesService.findAllByUserId(this.userId).pipe(take(1)),
      this.userStore.select(selectUser).pipe(take(1))
    ]).subscribe(([preferences, user]) => {
      preferences.forEach(preference => {
        if (this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative)) {
          preference.allDisabled = !this.notificationsVisible;
          this.notificationPreferencesService.updateById(preference.id, preference).subscribe();
        }
      });
    })
  }

  updateNotificationPreferences() {
    combineLatest([
      this.notificationPreferencesService.findAllByUserId(this.userId).pipe(take(1)),
      this.userStore.select(selectUser).pipe(take(1))
    ]).subscribe(([preferences, user]) => {
      const allDisabled = preferences.every(preference => preference.allDisabled);
      this.notificationsVisible = !allDisabled;

      // user notification selected number
      this.notificationsNumberProposal = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.PROPOSAL, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled
      ).length;

      this.notificationsNumberOffer = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.OFFER, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled
      ).length;

      this.notificationsNumberMemberArea = preferences.filter(preference =>
        this.notificationPreferencesService.isNotificationTriggerBelongingToNotificationType(NotificationType.MEMBER_AREA, preference.notificationTrigger) &&
        this.notificationPreferencesService.isUserAllowedToViewNotificationTrigger(preference.notificationTrigger, user.type, user.isRepresentative) &&
        preference.emailChannelEnabled).length;

      // expand notifications area
      this.notificationsProposalVisible = this.notificationsNumberProposal > 0;
      this.notificationsOfferVisible = this.notificationsNumberOffer > 0;
      this.notificationsMemberAreaVisible = this.notificationsNumberMemberArea > 0;
    });
  }

  // submitsSkenDataRegistration form
  // if user is already registered then it just jumps to skendata
  async jumpToSkenData() {
    await this.skendataService.submitModule1();
    await this.sleep(100);
    this.ngOnInit();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  protected readonly NotificationChannel = NotificationChannel;
  protected readonly Object = Object;
  protected readonly NotificationType = NotificationType;

}
